import React from 'react';
import { Alert, AlertTitle, Box, IconButton, Tooltip, Typography, keyframes } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { ScanAlert } from '../hooks/useScanAlerts';

/** Kasiyer ekrana baktığında şeridi gözden kaçırmasın diye hafif nabız efekti. */
const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(237, 108, 2, 0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(237, 108, 2, 0); }
  100% { box-shadow: 0 0 0 0 rgba(237, 108, 2, 0); }
`;

export interface ScanAlertBannerProps {
  alerts: ScanAlert[];
  /** Şeride tıklanınca açılacak detay listesi. */
  onOpen: () => void;
  /** Sağdaki çarpı: tüm uyarıları temizler. */
  onClearAll: () => void;
}

/**
 * Okutulduğu hâlde sepete eklenemeyen ürünlerin sepet bölümündeki kalıcı uyarısı.
 *
 * Eskiden bu durumda engelleyici bir modal açılıyordu; kasiyer ekrana bakmadan
 * okuttuğu için modalı görmüyor, modal da barkod okuyucunun tuşlarını yutup
 * sonraki ürünlerin sepete girmesini engelliyordu. Artık okutma hiç kesilmiyor,
 * uyarı burada birikiyor.
 */
const ScanAlertBanner: React.FC<ScanAlertBannerProps> = ({ alerts, onOpen, onClearAll }) => {
  if (alerts.length === 0) return null;

  const latest = alerts[0];

  return (
    <Alert
      // Sayaç değişince bileşen yeniden kurulur ve nabız animasyonu baştan oynar;
      // şerit zaten ekrandayken gelen yeni uyarı da gözden kaçmasın.
      key={alerts.length}
      severity="warning"
      variant="filled"
      onClick={onOpen}
      role="button"
      sx={{
        mb: 0.5,
        py: 0.25,
        cursor: 'pointer',
        alignItems: 'center',
        animation: `${pulse} 1.6s ease-out 3`,
        '& .MuiAlert-message': { width: '100%', py: 0.5, minWidth: 0 },
        '& .MuiAlert-icon': { py: 0.5 },
      }}
      action={
        <Tooltip title="Tüm uyarıları temizle">
          <IconButton
            size="small"
            color="inherit"
            aria-label="Tüm okutma uyarılarını temizle"
            onClick={(e) => {
              // Şeridin kendi onClick'i detay listesini açıyor
              e.stopPropagation();
              onClearAll();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <AlertTitle sx={{ m: 0, fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {alerts.length} ürün sepete eklenemedi
        </AlertTitle>
        <Typography
          variant="caption"
          sx={{ opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          Son: {latest.term} ({latest.title}) — detay için tıklayın
        </Typography>
      </Box>
    </Alert>
  );
};

export default ScanAlertBanner;
